import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { HiddenWork, LogMedia } from '@/api/endpoints';
import {
  Badge,
  Card,
  DataTable,
  DetailList,
  DetailSection,
  Drawer,
  ListGroup,
  ListRow,
  TableRow,
} from '@/components/ui';
import { MediaGrid, isVideo } from '@/features/daily-logs/LogList';
import { FullscreenViewer } from '@/features/drawings/FullscreenViewer';
import { LazyMediaViewer } from '@/features/drawings/LazyViewer';
import { RecordFlag } from '@/features/records/RecordTime';

/**
 * 隐蔽工程的三种看法 + 详情（CR-082）。「本工地」和「我的」共用。
 *
 * - `HiddenWorkTable`（电脑 xl 以上）：拍摄时间、类别、位置（唯一截断的一列）、拍摄人、前三张照片、服务器收到。
 *   **拍摄和服务器收到分两列**（一列只说一件事，CR-036）：绝大多数是同一分钟，差得多时后一列带「补传」标签。
 * - `HiddenWorkGrid`（「照片」看法）：一张封面 + 位置 + 类别 · 人 · 时间。找「那处拍得清不清楚」时比表格快。
 * - `HiddenWorkList`（窄屏、手机）：左边一张缩略图，位置、类别 · 人 · 时间。
 * - `HiddenWorkDetail`：电脑右侧 / 手机底部。照片 → 拍摄于 / 服务器收到 / 定位 / 拍摄人 → 说明。
 *
 * 「本工地」为什么默认是表不是图片墙（CR-031）：管理端真正要问的是「三楼防水是哪天做的」
 * 「上个月老陈拍的那批在哪」—— 那是**查找**问题。照片墙是加的第二种看法，不是默认。
 */

export function HiddenWorkTable({
  records,
  timezone,
  openId,
  onOpen,
}: {
  records: HiddenWork[];
  timezone: string;
  openId: string | null;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <DataTable
      minWidth="56rem"
      columns={[
        { key: 'captured', label: t('hidden.capturedColumn'), className: 'w-36' },
        { key: 'category', label: t('hidden.category'), className: 'w-28' },
        { key: 'location', label: t('hidden.location') },
        { key: 'user', label: t('hidden.byUser'), className: 'w-28' },
        { key: 'photos', label: t('hidden.photos'), className: 'w-48' },
        { key: 'server', label: t('hidden.serverColumn'), className: 'w-36' },
      ]}
    >
      {records.map((record) => (
        <TableRow key={record.id} selected={openId === record.id} onOpen={() => onOpen(record.id)}>
          <td className="whitespace-nowrap tabular-nums text-ink-2">
            <DateTime iso={record.capturedAt} timezone={timezone} />
          </td>
          <td className="whitespace-nowrap">
            <Badge tone="brand">{record.category}</Badge>
          </td>
          {/* 位置描述吃掉剩余宽度，长过一行才截（CR-035：不挤压） */}
          <td className="truncate font-medium text-ink">{record.locationDesc}</td>
          <td className="whitespace-nowrap text-ink-2">{record.user?.name}</td>
          <td className="whitespace-nowrap">
            <PhotoStrip photos={record.photos} />
          </td>
          <td className="whitespace-nowrap text-caption tabular-nums text-ink-3">
            <span className="inline-flex items-center gap-2">
              <Time iso={record.serverAt} timezone={timezone} />
              <RecordFlag recordedAt={record.serverAt} capturedAt={record.capturedAt} timeZone={timezone} />
            </span>
          </td>
        </TableRow>
      ))}
    </DataTable>
  );
}

/** 「照片」看法：封面网格 */
export function HiddenWorkGrid({
  records,
  timezone,
  onOpen,
}: {
  records: HiddenWork[];
  timezone: string;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {records.map((record) => {
        const cover = record.photos.find((p) => !isVideo(p)) ?? null;
        return (
          <li key={record.id}>
            <button type="button" onClick={() => onOpen(record.id)} className="block w-full text-left">
              <Card className="overflow-hidden transition-colors hover:border-rule-strong">
                <div className="relative aspect-[3/2] bg-surface-2">
                  {cover ? (
                    <img src={cover.thumbUrl ?? cover.url} alt="" loading="lazy" className="h-full w-full object-cover" />
                  ) : (
                    <span className="flex h-full items-center justify-center text-caption text-safety-600">{t('hidden.noPhoto')}</span>
                  )}
                  {record.photos.length > 1 && (
                    <span className="absolute bottom-1.5 left-1.5 rounded bg-black/50 px-1.5 text-micro leading-[18px] text-white">
                      {t('logs.photoCount', { count: record.photos.length })}
                    </span>
                  )}
                </div>
                <div className="px-3 py-2.5">
                  <span className="block truncate text-body font-semibold text-ink">{record.locationDesc}</span>
                  <span className="mt-0.5 flex items-center gap-1.5 truncate text-caption text-ink-3">
                    {record.category} · {record.user?.name} · <DateTime iso={record.capturedAt} timezone={timezone} />
                    <RecordFlag recordedAt={record.serverAt} capturedAt={record.capturedAt} timeZone={timezone} />
                  </span>
                </div>
              </Card>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** 窄屏、手机的列表 */
export function HiddenWorkList({
  records,
  timezone,
  onOpen,
  className,
}: {
  records: HiddenWork[];
  timezone: string;
  onOpen: (id: string) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  if (records.length === 0) return null;
  return (
    <ListGroup className={className}>
      {records.map((record) => {
        const cover = record.photos.find((p) => !isVideo(p)) ?? null;
        return (
          <ListRow
            key={record.id}
            onClick={() => onOpen(record.id)}
            leading={
              cover ? (
                <img src={cover.thumbUrl ?? cover.url} alt="" loading="lazy" className="size-12 rounded-lg border border-rule object-cover" />
              ) : (
                <span className="flex size-12 items-center justify-center rounded-lg border border-dashed border-rule text-center text-micro leading-tight text-safety-600">
                  {t('hidden.noPhoto')}
                </span>
              )
            }
            title={record.locationDesc}
            subtitle={
              <>
                {record.category} · {record.user?.name} · <DateTime iso={record.capturedAt} timezone={timezone} />
              </>
            }
            trailing={
              <>
                <RecordFlag recordedAt={record.serverAt} capturedAt={record.capturedAt} timeZone={timezone} />
                <span>{t('logs.photoCount', { count: record.photos.length })}</span>
              </>
            }
          />
        );
      })}
    </ListGroup>
  );
}

/** 一条隐蔽工程的详情 */
export function HiddenWorkDetail({
  record,
  timezone,
  onClose,
}: {
  record: HiddenWork | null;
  timezone: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [viewing, setViewing] = useState<LogMedia | null>(null);

  return (
    <>
      <Drawer
        open={record !== null}
        onClose={onClose}
        title={record?.locationDesc ?? ''}
        subtitle={record && [record.category, record.user?.name].filter(Boolean).join(' · ')}
      >
        {record && (
          <div className="flex flex-col gap-5">
            {record.photos.length > 0 ? (
              <MediaGrid media={record.photos} onOpenPhoto={setViewing} />
            ) : (
              <p className="text-body text-safety-600">{t('hidden.noPhoto')}</p>
            )}
            <DetailSection title={t('hidden.title')}>
              <DetailList
                items={[
                  // 拍摄时刻来自**手机**，服务器时刻是服务端记的（CR-033）。差得多说明是离线补传的，或者设备时钟不准
                  [t('hidden.capturedAtLabel'), <DateTime key="c" iso={record.capturedAt} timezone={timezone} full />],
                  [
                    t('hidden.serverColumn'),
                    <span key="s" className="inline-flex flex-wrap items-center gap-2">
                      <DateTime iso={record.serverAt} timezone={timezone} full />
                      <RecordFlag recordedAt={record.serverAt} capturedAt={record.capturedAt} timeZone={timezone} />
                    </span>,
                  ],
                  // 坐标可空，**渲染前必须判空**（约束 9）—— 拿 0 充数会显示成「0.000000, 0.000000」
                  record.lat != null &&
                    record.lng != null && [
                      t('hidden.gps'),
                      <span key="g" className="tabular-nums">
                        {record.lat.toFixed(6)}, {record.lng.toFixed(6)}
                        {record.gpsAccuracyM != null && ` · ±${Math.round(record.gpsAccuracyM)} m`}
                      </span>,
                    ],
                  record.user && [t('hidden.byUser'), record.user.name],
                  record.note && [t('hidden.note'), <span key="n" className="whitespace-pre-wrap">{record.note}</span>],
                ]}
              />
            </DetailSection>
          </div>
        )}
      </Drawer>

      {/* 和图纸、日报同一套全屏查看器：隐蔽工程照片放大看钢筋间距是常事 */}
      {viewing && record && (
        <FullscreenViewer
          title={`${record.category} · ${record.locationDesc}`}
          meta={new Date(record.capturedAt).toLocaleString(i18n.language, { timeZone: timezone })}
          onClose={() => setViewing(null)}
        >
          <LazyMediaViewer url={viewing.url} mimeType={viewing.mimeType ?? 'image/jpeg'} />
        </FullscreenViewer>
      )}
    </>
  );
}

/** 表格里的前三张照片 + 「+N」 */
function PhotoStrip({ photos }: { photos: HiddenWork['photos'] }) {
  const { t } = useTranslation();
  if (photos.length === 0) return <span className="text-caption text-safety-600">{t('hidden.noPhoto')}</span>;
  const shown = photos.filter((p) => !isVideo(p)).slice(0, 3);
  const rest = photos.length - shown.length;
  return (
    <span className="flex items-center gap-1">
      {shown.map((photo) => (
        <img key={photo.id} src={photo.thumbUrl ?? photo.url} alt="" loading="lazy" className="h-8 w-11 rounded border border-rule object-cover" />
      ))}
      {rest > 0 && <span className="pl-1 text-caption text-ink-3">+{rest}</span>}
    </span>
  );
}

/** 「9/17 09:40」；`full` 时带年份 */
function DateTime({ iso, timezone, full }: { iso: string; timezone: string; full?: boolean }) {
  const { i18n } = useTranslation();
  return (
    <span className="tabular-nums">
      {new Date(iso).toLocaleString(i18n.language, {
        timeZone: timezone,
        ...(full ? { year: 'numeric' } : {}),
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })}
    </span>
  );
}

function Time({ iso, timezone }: { iso: string; timezone: string }) {
  const { i18n } = useTranslation();
  return (
    <>
      {new Date(iso).toLocaleTimeString(i18n.language, { timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false })}
    </>
  );
}
